'use client'

import { useState } from 'react'
import { MarketingLayout } from '@/components/marketing/MarketingLayout'

export default function ContactPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    sector: '',
    message: '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormState('submitting')

    try {
      // Use Formspree or custom endpoint
      const formspreeId = process.env.NEXT_PUBLIC_FORMSPREE_ID
      const endpoint = formspreeId
        ? `https://formspree.io/f/${formspreeId}`
        : '/api/contact'

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(formData),
      })

      if (res.ok) {
        setFormState('success')
        setFormData({ name: '', email: '', organization: '', sector: '', message: '' })
      } else {
        setFormState('error')
      }
    } catch (e) {
      console.error('Contact form submission error:', e)
      setFormState('error')
    }
  }

  return (
    <MarketingLayout>
      <section className="contact-page">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <div className="eyebrow">Get in Touch</div>
              <h1>Let&apos;s figure out if we can help.</h1>
              <p>Tell us what you&apos;re working on. If Community Intelligence is the right fit, we&apos;ll show you what it can reveal. If not, we&apos;ll tell you that too.</p>
            </div>

            <div className="contact-form-card">
              <h2>Start a conversation</h2>
              <p>We&apos;ll get back to you within one business day.</p>

              {formState === 'success' && (
                <div className="success-message">
                  Thanks for reaching out! We&apos;ll be in touch soon.
                </div>
              )}

              {formState === 'error' && (
                <div className="error-message">
                  Something went wrong. Please try again or email us directly.
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="Your name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="you@company.com"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="organization">Organization</label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    placeholder="Your company or agency"
                    value={formData.organization}
                    onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="sector">Sector</label>
                  <select
                    id="sector"
                    name="sector"
                    value={formData.sector}
                    onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                  >
                    <option value="" disabled>Select your sector</option>
                    <option value="government">Government & Policy</option>
                    <option value="healthcare">Healthcare & Public Health</option>
                    <option value="development">Real Estate & Development</option>
                    <option value="insurance">Insurance & Risk</option>
                    <option value="education">Education</option>
                    <option value="campaigns">Civic Campaigns</option>
                    <option value="investment">Impact Investment</option>
                    <option value="environmental">Environmental & Conservation</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="message">What are you working on?</label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Tell us about the community challenge or opportunity you're trying to understand..."
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  />
                </div>
                <button type="submit" className="btn-submit" disabled={formState === 'submitting'}>
                  {formState === 'submitting' ? 'Sending...' : 'Send Message'}
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  )
}
